import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useApi } from "@/api/hooks/useApi";
import type {
  ConnectedProjectDto,
  CopilotPromptConfigurationsDto,
  ILoqProjectConfigurationDto,
  RemsProjectConfigurationDto,
} from "@/api/types";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { QUERY_KEYS } from "@/query-keys";

import {
  getPortfolioIntegrationProjectStatus,
  isILoqConfigured,
  isILoqSetupComplete,
  isRemsConfigured,
  isRemsSetupComplete,
} from "./helpers";
import type { PortfolioIntegrationConfiguration, PortfolioIntegrationProject } from "./types";

type PortfolioConfigurationResult<TConfiguration extends PortfolioIntegrationConfiguration> = {
  projectId: string;
  configuration: TConfiguration;
  canManage?: boolean;
};

type PortfolioIntegrationProjectsResult<TConfiguration extends PortfolioIntegrationConfiguration> = {
  projects: ConnectedProjectDto[];
  rows: PortfolioIntegrationProject<TConfiguration>[];
  isLoading: boolean;
  error: Error | null;
};

type CreateProjectRow<TConfiguration extends PortfolioIntegrationConfiguration> = (
  project: ConnectedProjectDto,
  configuration: TConfiguration,
) => PortfolioIntegrationProject<TConfiguration>;

/** Loads AI configuration only for connected projects the current user can manage. */
export function usePortfolioAiProjects(): PortfolioIntegrationProjectsResult<CopilotPromptConfigurationsDto> {
  const api = useApi();

  return usePortfolioProjects("ai", async () => (await api.getPortfolioIntegrationsAiV1()).data, createAiProjectRow);
}

/** Loads REMS configuration for every connected project. */
export function usePortfolioRemsProjects(): PortfolioIntegrationProjectsResult<RemsProjectConfigurationDto> {
  const api = useApi();

  return usePortfolioProjects(
    "rems",
    async () => (await api.getPortfolioIntegrationsRemsV1()).data,
    createRemsProjectRow,
  );
}

/** Loads iLOQ configuration for every connected project. */
export function usePortfolioILoqProjects(): PortfolioIntegrationProjectsResult<ILoqProjectConfigurationDto> {
  const api = useApi();

  return usePortfolioProjects(
    "iloq",
    async () => (await api.getPortfolioIntegrationsIloqV1()).data,
    createILoqProjectRow,
  );
}

function usePortfolioProjects<TConfiguration extends PortfolioIntegrationConfiguration>(
  queryKey: "ai" | "rems" | "iloq",
  loadConfigurations: () => Promise<PortfolioConfigurationResult<TConfiguration>[]>,
  createProjectRow: CreateProjectRow<TConfiguration>,
): PortfolioIntegrationProjectsResult<TConfiguration> {
  const selfAccountQueries = useSelfAccountQueries();
  const projectsQuery = useQuery(selfAccountQueries.listLinkedProjects());
  const projects = useMemo(
    () => (projectsQuery.data ?? []).filter((project) => project.userRole !== "resident"),
    [projectsQuery.data],
  );
  const projectIds = useMemo(() => projects.map((project) => project.id), [projects]);
  const configurationQuery = useQuery({
    queryKey: [...QUERY_KEYS.PORTFOLIO_INTEGRATION_CONFIGURATIONS(queryKey), projectIds],
    enabled: projectsQuery.isSuccess && projects.length > 0,
    queryFn: loadConfigurations,
  });
  const rows = useMemo<PortfolioIntegrationProject<TConfiguration>[]>(() => {
    const configurationByProjectId = new Map(
      configurationQuery.data?.map((configuration) => [configuration.projectId, configuration]),
    );

    return projects.flatMap((project) => {
      const result = configurationByProjectId.get(project.id);
      if (!result) {
        return [];
      }
      if (result.canManage === false) {
        return [
          {
            project,
            configuration: result.configuration,
            configured: false,
            enabled: false,
            setupComplete: false,
            status: "unavailable" as const,
            canManage: false,
          },
        ];
      }

      return [createProjectRow(project, result.configuration)];
    });
  }, [configurationQuery.data, createProjectRow, projects]);

  return {
    projects,
    rows,
    isLoading: projectsQuery.isLoading || (projects.length > 0 && configurationQuery.isLoading),
    error: projectsQuery.error ?? configurationQuery.error ?? null,
  };
}

function createAiProjectRow(
  project: ConnectedProjectDto,
  configuration: CopilotPromptConfigurationsDto,
): PortfolioIntegrationProject<CopilotPromptConfigurationsDto> {
  const configured = configuration.configurations.length > 0;

  return {
    project,
    configuration,
    configured,
    enabled: true,
    setupComplete: true,
    status: configured ? "enabled" : "notInUse",
    canManage: true,
  };
}

function createRemsProjectRow(
  project: ConnectedProjectDto,
  configuration: RemsProjectConfigurationDto,
): PortfolioIntegrationProject<RemsProjectConfigurationDto> {
  const configured = isRemsConfigured(configuration);
  const enabled = configuration.tickets.enabled || configuration.contracts.enabled;
  const setupComplete = isRemsSetupComplete(configuration);

  return {
    project,
    configuration,
    configured,
    enabled,
    setupComplete,
    status: getPortfolioIntegrationProjectStatus({ configured, enabled, setupComplete }),
    canManage: true,
  };
}

function createILoqProjectRow(
  project: ConnectedProjectDto,
  configuration: ILoqProjectConfigurationDto,
): PortfolioIntegrationProject<ILoqProjectConfigurationDto> {
  const configured = isILoqConfigured(configuration);
  const enabled = configuration.enabled;
  const setupComplete = isILoqSetupComplete(configuration);

  return {
    project,
    configuration,
    configured,
    enabled,
    setupComplete,
    status: getPortfolioIntegrationProjectStatus({ configured, enabled, setupComplete }),
    canManage: true,
  };
}
